import { useState } from 'preact/hooks'
import { FileCheck, Activity, UserX } from 'lucide-preact'
import { AssetWorkflowSettings } from '@/components/webs/panels/AssetWorkflowSettings'
import { TrackingPanel } from '@/components/tracking/TrackingPanel'
import { VisitorAnalyticsPanel } from '@/components/dashboard/analytics/VisitorAnalyticsPanel'
import { NonProspectDomainsCard } from '@/components/nonProspectDomains/NonProspectDomainsCard'

interface Props {
  clientCompanyId: string
  workspaceId: string
}

type SettingsSection = 'workflow' | 'tracking' | 'nonProspects'

interface SettingsNavItem {
  key: SettingsSection
  label: string
  icon: typeof FileCheck
  description: string
}

const navItems: SettingsNavItem[] = [
  {
    key: 'workflow',
    label: 'Asset Workflow',
    icon: FileCheck,
    description: 'Default asset approval workflow for this workspace'
  },
  {
    key: 'tracking',
    label: 'Tracking',
    icon: Activity,
    description: 'Install visitor tracking on this workspace'
  },
  {
    key: 'nonProspects',
    label: 'Non-Prospects',
    icon: UserX,
    description: 'Domains not counted as prospects here'
  }
]

export function WorkspaceSettingsPage({ clientCompanyId, workspaceId }: Props) {
  const [activeSection, setActiveSection] = useState<SettingsSection>('workflow')

  return (
    <div className="p-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 dark:text-white">
            Workspace Settings
          </h1>
          <p className="text-slate-600 dark:text-slate-300 mt-1">
            Configure default settings for all campaigns and webs in this workspace
          </p>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar nav - mirrors WebSettingsTab pattern */}
        <nav className="lg:w-56 flex-shrink-0">
          {/* Mobile: Horizontal tabs */}
          <div className="lg:hidden flex gap-2 border-b border-slate-200 dark:border-slate-700 pb-2">
            {navItems.map((item) => {
              const Icon = item.icon
              const isActive = activeSection === item.key
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setActiveSection(item.key)}
                  className={`flex-1 flex flex-col items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                    isActive
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </button>
              )
            })}
          </div>

          {/* Desktop: Vertical sidebar */}
          <div className="hidden lg:block space-y-1">
            <h3 className="px-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
              Settings
            </h3>
            {navItems.map((item) => {
              const Icon = item.icon
              const isActive = activeSection === item.key
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setActiveSection(item.key)}
                  className={`w-full flex items-start gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 shadow-sm'
                      : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                  }`}
                >
                  <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isActive ? 'text-primary-600 dark:text-primary-500' : 'text-slate-500 dark:text-slate-400'}`} />
                  <div className="flex-1 text-left">
                    <div className="font-medium">{item.label}</div>
                    <div className={`text-xs mt-0.5 ${isActive ? 'text-primary-600/80 dark:text-primary-400/80' : 'text-slate-500 dark:text-slate-400'}`}>
                      {item.description}
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        </nav>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {activeSection === 'workflow' && (
            <AssetWorkflowSettings
              level="workspace"
              clientCompanyId={clientCompanyId}
              workspaceId={workspaceId}
            />
          )}
          {activeSection === 'tracking' && (
            <div className="space-y-6">
              <TrackingPanel
                scope="workspace"
                clientCompanyId={clientCompanyId}
                workspaceId={workspaceId}
              />
              <VisitorAnalyticsPanel scope="workspace" workspaceId={workspaceId} />
            </div>
          )}
          {activeSection === 'nonProspects' && (
            <NonProspectDomainsCard
              scope={{ level: 'workspace', clientCompanyId, workspaceId }}
            />
          )}
        </div>
      </div>
    </div>
  )
}
